import React, { useCallback } from 'react';
import {
    Button,
    ColumnFilterButton,
    FilterOptionList,
    FilterPopover,
    Input,
} from '@/components/ui';
import type { FilterPopoverTriggerState } from '@/components/ui';
import { AuditDatePresetList } from './AuditDatePresetList';
import type { AuditFilterOption } from './AuditFilterChip';
import type { AuditDatePresetLabels, AuditDateRangeValue } from './auditDatePresets';
import { CrossIcon } from './icons';
import styles from './AuditFilters.module.css';

/**
 * EAA-1821: the audit table's per-column control — the thin binding between
 * this screen's filter shape and the shared kit.
 *
 * Everything visual here comes from `packages/ui`: `ColumnFilterButton` is the
 * 26px trigger (and owns the inactive/active language), `FilterPopover` is the
 * anchored menu, `FilterOptionList` is the multi-select body the FILTER BAR's
 * chip also renders. What stays in the app is the part that is not reusable —
 * which audit facet each variant writes to, and the date vocabulary.
 *
 * Modelled as a discriminated union rather than a bag of optionals so the
 * compiler enforces the pairing: `variant="search"` cannot be handed a
 * `string[]`, and `variant="filter"` cannot be rendered without `options`. A
 * `value?: string | string[]` plus `options?` would have compiled for every
 * wrong combination and failed at runtime instead.
 */

interface AuditColumnFilterBaseProps {
    /** This column currently narrows the table — paints the brand-tint state. */
    active: boolean;
    /** Tooltip + accessible name of the trigger, e.g. "Lọc theo hành động". */
    title: string;
    /** Copy for the popover's clear action. */
    clearLabel: string;
}

export interface AuditColumnSelectFilterProps extends AuditColumnFilterBaseProps {
    variant: 'filter';
    options: AuditFilterOption[];
    value: string[];
    onChange: (next: string[]) => void;
}

export interface AuditColumnSearchFilterProps extends AuditColumnFilterBaseProps {
    variant: 'search';
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
}

export interface AuditColumnDateFilterProps extends AuditColumnFilterBaseProps {
    variant: 'date';
    value: AuditDateRangeValue;
    onChange: (next: AuditDateRangeValue) => void;
    /**
     * EAA-1821: the translated preset copy, same object the filter BAR's
     * `AuditDateRangeChip` gets. Without it `AuditDatePresetList` falls back to
     * the hardcoded Vietnamese `AUDIT_DATE_PRESETS`, which is what made the
     * header funnel read "Hôm nay / 7 ngày" while the bar one column-strip away
     * read "Today / 7 days" with the console set to English.
     */
    labels?: AuditDatePresetLabels;
}

export type AuditColumnFilterProps =
    | AuditColumnSelectFilterProps
    | AuditColumnSearchFilterProps
    | AuditColumnDateFilterProps;

function AuditColumnSearchBody({
    value,
    onChange,
    placeholder,
    clearLabel,
    title,
}: AuditColumnSearchFilterProps): React.ReactElement {
    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
        [onChange]
    );
    const handleClear = useCallback(() => onChange(''), [onChange]);

    return (
        <div className={styles.colSearch}>
            {/* EAA-1821: autoFocus is correct here and not a nuisance — the box
                only exists once the operator has deliberately opened it, and
                without it every column search costs an extra click. */}
            <Input
                className={styles.colSearchInput}
                autoFocus
                value={value}
                placeholder={placeholder}
                aria-label={title}
                onChange={handleChange}
            />
            {value ? (
                <Button className={styles.colSearchClear} onClick={handleClear}>
                    <CrossIcon size={12} />
                    {clearLabel}
                </Button>
            ) : null}
        </div>
    );
}

function AuditColumnFilterBase(props: AuditColumnFilterProps): React.ReactElement {
    const { variant, active, title } = props;

    const trigger = ({ open, ref, toggle }: FilterPopoverTriggerState) => (
        <ColumnFilterButton
            ref={ref}
            icon={variant === 'search' ? 'search' : 'filter'}
            active={active}
            open={open}
            title={title}
            onClick={toggle}
        />
    );

    let body: React.ReactNode;
    if (props.variant === 'filter') {
        body = (
            <FilterOptionList
                label={title}
                options={props.options}
                value={props.value}
                onChange={props.onChange}
                clearLabel={props.clearLabel}
            />
        );
    } else if (props.variant === 'date') {
        body = (
            <AuditDatePresetList
                label={title}
                value={props.value}
                onChange={props.onChange}
                labels={props.labels}
            />
        );
    } else {
        body = <AuditColumnSearchBody {...props} />;
    }

    // EAA-1821: `align="end"` — this popover lives INSIDE the table's scroll
    // region, so a left-anchored 220px menu on a right-hand column would be
    // clipped by that region rather than overflowing the page.
    return (
        <FilterPopover className={styles.colFilterRoot} align="end" trigger={trigger}>
            {body}
        </FilterPopover>
    );
}

/**
 * EAA-1821: memoized because it lives in a table header that re-renders on
 * every page fetch, every sort flip and every keystroke in the filter bar.
 * Callers must therefore pass `useCallback`'d `onChange` and `useMemo`'d
 * `options`/`value` — `AuditTable` does.
 */
export const AuditColumnFilter = React.memo(AuditColumnFilterBase);
